import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import { Stack, styled, Grid, DialogTitle } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { useForm, FormProvider } from 'react-hook-form';
import {
  getRecruiterTemplates,
  ICVBulkUploadDTO,
  IExcelImportTemplate,
  processExcelRecords,
  readExcelRecords,
  uploadFileToS3,
} from 'wisestep-api';
import {
  Spinner,
  CardVariant,
  WsCard,
  IUserDTO,
  WsFileUpload,
  FileTypeVariants,
  WsDialog,
} from 'wisestep-base';

import { useUserContext } from '../../../context';
import CreateExcelTemplate, {
  CreateExcelTemplateProps,
} from '../../../features/emailSettings/components/templates/CreateExcelTemplate';
import { IExcelRecords } from '../../../features/importCandidates/types';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import {} from '../../../features/importCandidates/utils/bulkSubmissionsHelper';
import { getFavouredExcelTemplatesFirst } from '../helper/TemplateSettingsHelper';

import ExcelTemplateListingTable from './ExcelTemplateListingTable';

const Styles = styled(Stack)({ padding: '0', gap: 0.5 });

interface FileUploadProgress {
  file: File;
  progress?: number;
  error?: string;
  processing?: boolean;
  completed?: boolean;
}

const ExcelTemplateSettings = () => {
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showUploadPopUp, setshowUploadPopUp] = useState(false);
  const handleAddDrawer = () => setshowUploadPopUp(true);
  const closeDialog = () => setShowCreateTemplate(false);
  const closeUploadDialog = () => setshowUploadPopUp(false);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [refresh, setRefresh] = useState<boolean>(false);
  const [flagType, setFlagType] = useState<CreateExcelTemplateProps['variant']>('new');
  const [template, setTemplate] = useState<IExcelImportTemplate[]>([]);
  const [editOrCloneData, setEditOrCloneData] = useState<IExcelImportTemplate>();
  const [loadPage, setLoadPage] = useState<boolean>(false);
  const [hasData, setHasData] = useState<boolean>(false);
  const [selectedCandidatesFile, setSelectedCandidatesFile] = useState<FileUploadProgress | null>();
  const [excelFields, setExcelFields] = useState<Map<number, IExcelRecords>>();

  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm();

  const { setValue, getValues } = methods;

  const handleDrawer = (flag: string, file: string) => {
    (flag === 'edit' || flag === 'clone') && readExcelRecordsByFileId(file);
    setShowCreateTemplate(true);
  };

  const onFileUpload = async (files: File[]) => {
    setSelectedCandidatesFile({ file: files[0], progress: 0 });
    setshowUploadPopUp(false);
    try {
      const response = await uploadFileToS3(files[0], (event: any) => {
        const progress = { ...selectedCandidatesFile, file: files[0] };
        progress.progress = (100 * event.loaded) / event.total;
        setSelectedCandidatesFile(progress);
      });
      const url = response;
      const processRequest: ICVBulkUploadDTO = {
        resumeFileName: files[0].name,
        resumeURL: url.split('?')[0],
        isJobCandidateMapping: true,
      };
      setSelectedCandidatesFile({
        ...selectedCandidatesFile,
        progress: selectedCandidatesFile?.progress,
        file: files[0],
        processing: true,
      });
      const processResponse = await processExcelRecords(processRequest);
      const filedId = processResponse.data.fileId;
      setSelectedCandidatesFile({
        ...selectedCandidatesFile,
        progress: selectedCandidatesFile?.progress,
        file: files[0],
        completed: true,
      });
      await readExcelRecordsByFileId(filedId);
    } catch (e) {
      setNotification(
        failed('The data format provided is invalid. Please try again using a valid format.')
      );
      setSelectedCandidatesFile(null);
    }
  };

  const readExcelRecordsByFileId = useCallback(
    async (fileId: string) => {
      setValue('fileId', fileId);
      try {
        const excelResponse = await readExcelRecords(fileId);
        setExcelFields(excelResponse.data);
        setShowCreateTemplate(true);
      } catch (e) {
        setShowCreateTemplate(true);
      }
    },
    [setValue]
  );

  const onFileRejected = (files: FileRejection[]) => {
    files.map((file) => {
      file.errors.map((error) => {
        console.log('rejected' + error.message);
      });
    });
  };

  useEffect(() => {
    (async function () {
      const defaultTemplateData = await getRecruiterTemplates(true);
      setHasData(true);
      setTemplate(getFavouredExcelTemplatesFirst(defaultTemplateData.data, loggedInUser));
      setLoadPage(true);
    })();
  }, [loggedInUser, refresh]);

  const getLatestData = () => {
    setRefresh(!refresh);
  };

  return (
    <>
      <Styles>
        <Grid direction="column">
          <Grid>
            {!loadPage ? (
              <Spinner />
            ) : !hasData ? (
              <WsCard
                cardType={CardVariant.WITH_OUT_BG}
                title="No Templates Found"
                description=""
                middleComponent={<FilePresentOutlinedIcon />}
                isButton={true}
                onClick={() => {
                  setFlagType('new');
                  handleAddDrawer();
                }}
                buttonText="Create New Template"
              />
            ) : (
              <ExcelTemplateListingTable
                templates={template}
                setEditOrCloneData={setEditOrCloneData}
                getLatestData={getLatestData}
                handleDrawer={handleDrawer}
                handleAddDrawer={handleAddDrawer}
                getFlagType={(flag) => setFlagType(flag)}
              />
            )}
          </Grid>
        </Grid>
      </Styles>
      {showUploadPopUp && (
        <WsDialog openDialog={showUploadPopUp} onClose={closeUploadDialog}>
          <DialogTitle
            sx={{
              justifyContent: 'center',
            }}
          >
            Upload Excel sheet
          </DialogTitle>
          <DialogTitle>
            <Grid>
              <WsFileUpload
                name="CandidateSpreadSheets"
                onUpload={onFileUpload}
                placeholderText={'Drag & drop .xls,.xlvs or .csv file < 1MB'}
                fileTypes={FileTypeVariants.BULKUPLOAD}
                onFileRejected={onFileRejected}
                maxFileSize={2}
              />
            </Grid>
          </DialogTitle>
        </WsDialog>
      )}
      {showCreateTemplate && (
        <FormProvider {...methods}>
          <CreateExcelTemplate
            openDialog={showCreateTemplate}
            closeDialog={closeDialog}
            variant={flagType}
            onEditOrCloneTemplate={editOrCloneData}
            candidatesData={excelFields}
            getLatestData={getLatestData}
            fileId={getValues('fileId')}
          ></CreateExcelTemplate>
        </FormProvider>
      )}
    </>
  );
};

export default ExcelTemplateSettings;
